/**
 * Helper: GraphQL
 * -----------------------------------------------------------------------------
 * Handles GraphQL responses. Query helpers.
 * - Add in alphabetical order.
 *
 */

import { convertToCamelCase } from '~/helpers/convert'
import { getMetafieldIdFromString } from '~/helpers/general'

/**
 * Decodes Shopify API IDs into Liquid format IDs.
 * @param {String} string - Encoded string.
 * @returns {String}
 */
export function decodeApiId(string) {
  if (!string) {
    return null
  }

  return Number(string.split('/').pop())
}

/**
 * Get metafield identifiers to use in Storefront API queries
 * @param {String} namespace - The object that the metafields belong to,
 * such as product or variant
 * @returns {{namespace: String, key: String}[]}
 */
export function getMetafieldIDs(namespace) {
  const metafields = cnvs.metafields[namespace] || []
  return metafields.map(getMetafieldIdFromString)
}

/**
 * Get variables commonly used in Storefront API queries.
 * @param {Object} options - Any additional options to add to the variables,
 * such as handle
 * @returns {{country: string, language: string}}
 */
export function getVariables(options = {}) {
  return {
    country: cnvs.store.country,
    language: cnvs.store.language,
    ...options,
  }
}

/**
 * Removes edges and nodes from GraphQL data.
 * @param {Object} object - GraphQL data.
 * @param {String} [field] - Optional string to filter by.
 * @returns {Object}
 */
export function transformEdges(object, field) {
  if (!object) {
    return []
  }

  if (!field) {
    return object.edges.map((edge) => {
      return edge.node
    })
  }

  return object.edges.map((edge) => {
    return edge.node[field]
  })
}

/**
 * Transform metaobject fields into a key, value map. The keys are converted
 * to camelCase
 * @param {Array} fields - Fields from metaobject query
 * @param {Array} [excludedKeys] - Keys to ignore in the final result
 * @returns {Object}
 */
export function transformMetaobjectFields(fields, excludedKeys = []) {
  const result = {}
  fields?.forEach(({ key, value, reference, references }) => {
    if (!key || (Array.isArray(excludedKeys) && excludedKeys.includes(key))) {
      return
    }
    const camelKey = convertToCamelCase(key.replace(/_/g, ' '))
    result[camelKey] = references?.nodes || reference || value
    if (references?.nodes && references.nodes[0]?.fields) {
      result[camelKey] = result[camelKey].map(
        (node) => transformMetaobjectFields(node.fields, excludedKeys),
      )
    }
  })
  return result
}
